import { Link } from "react-router-dom";
import { AnimateOnScroll } from "../../../components/animate";
import { AnimateWordsOnScroll } from "../../../components/animate/animateWords";
import { EmptyState } from "../../../components/empty";
import { useFindEventsQuery } from "../../../redux/features/event/event.api";
import { useGetIpLocationQuery } from "../../../redux/features/geo/geo.api";
import { IpLocation } from "../../../redux/features/geo/geo.types";
import { formatDate } from "../../../utils/helpers";
import { EventCard } from "./eventCard";

const eventCardSkeletonKeys = ["a", "b", "c", "d", "e"];

const getFindEventsHeading = (location?: IpLocation | null) => {
  const city = location?.city?.toLowerCase() ?? "";
  const state = location?.state?.toLowerCase() ?? "";
  const country = location?.country?.toLowerCase() ?? "";

  const isInLagos = city.includes("lagos") || state.includes("lagos");

  if (isInLagos) return { prefix: "Find events in", highlight: "Lagos" };
  if (country === "nigeria")
    return { prefix: "Find events around", highlight: "Lagos" };
  if (country === "canada")
    return { prefix: "Find events in", highlight: "Canada" };

  return { prefix: "Find events", highlight: "you'd like" };
};

export const FindEventsSection = () => {
  const { data: ipLocation } = useGetIpLocationQuery();

  const {
    data: eventsResponse,
    isFetching,
    isError,
    refetch,
  } = useFindEventsQuery(
    {
      page: 1,
      limit: 5,
      sortOrder: "asc",
      startDate: formatDate(new Date(), "YYYY-MM-DD"),
    },
    { refetchOnMountOrArgChange: true },
  );

  const events = (eventsResponse?.data?.data ?? []).slice(0, 5);
  const hasResolved = !isFetching && !isError;
  const { prefix, highlight } = getFindEventsHeading(ipLocation);

  if (hasResolved && events.length === 0) return null;

  return (
    <section className="max-width-website py-12 md:py-16">
      <div className="flex items-start justify-between gap-4">
        <AnimateWordsOnScroll
          key={`${prefix}-${highlight}`}
          as="h2"
          className="font-dela text-[28px] leading-[1.15] text-[#7E8999] md:text-[44px]"
        >
          {prefix} <span className="text-ink">{highlight}</span>
        </AnimateWordsOnScroll>

        <AnimateOnScroll
          animation="fade-up"
          delay={0.2}
          className="hidden md:block"
        >
          <Link
            to="/find-events"
            className="rounded-full border border-grey-200 px-5 py-2.5 font-onest text-base font-medium text-ink transition-colors duration-300 hover:bg-white"
          >
            See more
          </Link>
        </AnimateOnScroll>
      </div>

      {isError ? (
        <div className="mt-8">
          <EmptyState
            text="Events Couldn't Load"
            subtext="We couldn't reach the event service. Please try again shortly."
            className="w-full !bg-transparent"
            image="/assets/images/search-glass.png"
            ctaText="Retry"
            onCTAClick={() => {
              refetch();
            }}
          />
        </div>
      ) : (
        <>
          <div className="mt-8 flex flex-col gap-6 md:grid md:grid-cols-5 md:gap-4">
            {isFetching && !events.length
              ? eventCardSkeletonKeys.map((key) => (
                  <div
                    key={key}
                    className="aspect-square w-full animate-pulse rounded-[6px] bg-grey-100 md:aspect-auto md:h-[280px]"
                  />
                ))
              : events.map((event, idx) => (
                  <AnimateOnScroll
                    key={event.slug || event.name}
                    animation="fade-up"
                    delay={0.1 + idx * 0.04}
                  >
                    <EventCard event={event} />
                  </AnimateOnScroll>
                ))}
          </div>

          <Link
            to="/find-events"
            className="mt-8 inline-block rounded-full border border-grey-200 px-5 py-2.5 font-onest text-base font-medium text-ink transition-colors duration-300 hover:bg-white md:hidden"
          >
            See more
          </Link>
        </>
      )}
    </section>
  );
};
